import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@fametrue/shared';
import { AcContent, AcLabel, AcList } from './Accordion';
import { DamageCard } from './DamageCard';
import {
  PlatformCards,
  PlatformIntelligence,
  availablePlatforms,
  extraIntelligencePlatforms,
  platformName,
} from './PlatformCards';
import { formatEuro } from './palette';

/**
 * Level 2 — quali card esistono per un brand, in che ordine e con che numero.
 *
 * Su richiesta del committente (16/10, Level 2 punto 3) ogni card si mostra
 * solo se nel JSON ci sono i suoi dati, e la numerazione si calcola sulle card
 * effettivamente presenti, **senza buchi**: se manca la 05, la vecchia 06
 * diventa 05. Prima i numeri erano letterali e il numero saltava.
 *
 * Il contatore è uno solo e attraversa tutte e quattro le sezioni, che è il
 * motivo per cui questo elenco non può vivere dentro un componente di sezione:
 * `DiagnosisBlocks`, che conosceva solo la propria, è stato sciolto.
 *
 * La chiave i18n di ogni card è il suo **identificativo**, non il numero: dopo
 * questa modifica `forum` si mostra come 05, 06 o 07 a seconda del brand, e una
 * chiave chiamata `b07` sarebbe una bugia da tenere allineata a mano.
 */
export const DECISION_CARD_IDS = [
  'mechanism',
  'touchpoint',
  'verdict',
  'dichotomy',
  'extraPlatforms',
  'sentiment',
  'forum',
  'fameRiskDetail',
  'actionPlan',
  'roadmap',
] as const;
export type DecisionCardId = (typeof DECISION_CARD_IDS)[number];

export type DecisionSectionId = 'diagnosis' | 'platformDetail' | 'economicExposure' | 'remediation';

export interface DecisionCard {
  id: DecisionCardId;
  /** Progressivo sulle card presenti, con lo zero davanti: '01', '02'… */
  num: string;
  body: ReactNode;
}

export interface DecisionSection {
  id: DecisionSectionId;
  titleKey: string;
  cards: DecisionCard[];
  /** Contenuti senza numero: griglia delle piattaforme, card del danno. */
  extras: ReactNode | null;
}

type Level2 = Brand['level2'];

/**
 * Funzione pura, non un componente: non chiama `t` e non usa hook. Le etichette
 * le risolve chi rende, così lo stesso elenco si può verificare a test senza
 * montare nulla.
 *
 * La condizione di presenza e il corpo della card si scrivono nello stesso
 * `if`: la verità sta in un posto solo e TypeScript restringe il tipo da sé,
 * senza asserzioni.
 */
export function decisionSections(brand: Brand): DecisionSection[] {
  const l2 = brand.level2;
  let n = 0;

  const push = (cards: DecisionCard[], id: DecisionCardId, body: ReactNode): void => {
    n += 1;
    cards.push({ id, num: String(n).padStart(2, '0'), body });
  };

  const diagnosis: DecisionCard[] = [];
  push(diagnosis, 'mechanism', <MechanismBody mechanism={l2.mechanism} />);
  push(diagnosis, 'touchpoint', <TouchpointBody touchpoint={l2.touchpoint} />);
  push(diagnosis, 'verdict', <VerdictBody verdict={l2.verdict} />);
  push(diagnosis, 'dichotomy', <DichotomyBody dichotomy={l2.dichotomy} />);

  const extra = extraIntelligencePlatforms(brand);
  if (extra.length > 0) {
    push(diagnosis, 'extraPlatforms', <ExtraPlatformsBody platforms={extra} />);
  }

  // Il sentiment c'è solo se almeno una delle due colonne ha qualcosa: un
  // blocco presente con entrambi gli elenchi vuoti renderebbe una card vuota.
  const sentiment = l2.sentiment;
  if (sentiment && (sentiment.positivi.length > 0 || sentiment.negativi.length > 0)) {
    push(diagnosis, 'sentiment', <SentimentBody sentiment={sentiment} />);
  }

  const forum = l2.forum;
  if (forum) push(diagnosis, 'forum', <ForumBody forum={forum} />);

  const platforms = availablePlatforms(brand);

  const exposure: DecisionCard[] = [];
  const frd = l2.fameRiskDetail;
  if (frd) push(exposure, 'fameRiskDetail', <RiskDetailBody detail={frd} />);

  const remediation: DecisionCard[] = [];
  const actionPlan = l2.actionPlan;
  if (actionPlan) push(remediation, 'actionPlan', <ActionPlanBody plan={actionPlan} />);
  const roadmap = l2.roadmap;
  if (roadmap) push(remediation, 'roadmap', <RoadmapBody roadmap={roadmap} />);

  return [
    { id: 'diagnosis', titleKey: 'decision.diagnosis', cards: diagnosis, extras: null },
    {
      id: 'platformDetail',
      titleKey: 'decision.platformDetail',
      cards: [],
      extras:
        platforms.length > 0 ? (
          <div className="platform-grid">
            <PlatformCards brand={brand} />
          </div>
        ) : null,
    },
    {
      id: 'economicExposure',
      titleKey: 'decision.economicExposure',
      cards: exposure,
      extras: brand.reputationalDamage.available ? <DamageCard brand={brand} /> : null,
    },
    { id: 'remediation', titleKey: 'decision.remediation', cards: remediation, extras: null },
  ];
}

/* ---------- i dieci corpi ---------- */

function MechanismBody({ mechanism }: { mechanism: Level2['mechanism'] }): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <AcContent html={mechanism.content} blocks={mechanism.blocks} />
      {mechanism.stats && mechanism.stats.length > 0 ? (
        <>
          <AcLabel>{t('decision.keyData')}</AcLabel>
          <AcList
            items={mechanism.stats.map((s) => (
              <>
                <strong>{s.label}</strong>: {s.value}
              </>
            ))}
          />
        </>
      ) : null}
    </>
  );
}

const TOUCHPOINT_COLORS: Record<string, string> = {
  presidiato: 'var(--green)',
  parziale: 'var(--amber)',
  assente: 'var(--red)',
};

function TouchpointBody({ touchpoint }: { touchpoint: Level2['touchpoint'] }): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      {touchpoint.items.map((item) => (
        <div key={item.name}>
          <AcLabel>{item.name}</AcLabel>
          <div
            className="ac-stat"
            style={{
              borderLeft: `3px solid ${TOUCHPOINT_COLORS[item.status] ?? 'var(--border)'}`,
              paddingLeft: 12,
            }}
          >
            {t(`decision.touchpoint.${item.status}`)}
          </div>
          <p>{item.desc}</p>
        </div>
      ))}
    </>
  );
}

function VerdictBody({ verdict }: { verdict: Level2['verdict'] }): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <div
        className="ac-stat"
        style={{
          background: 'var(--red-light)',
          color: 'var(--red-dark)',
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {t('decision.cherryPicking')}: {verdict.result}
      </div>
      <AcLabel>{t('decision.supportingSignals')}</AcLabel>
      <AcList items={verdict.signals} />
    </>
  );
}

function DichotomyBody({ dichotomy }: { dichotomy: Level2['dichotomy'] }): ReactNode {
  return <AcContent html={dichotomy.content} blocks={dichotomy.blocks} />;
}

function ExtraPlatformsBody({
  platforms,
}: {
  platforms: ReturnType<typeof extraIntelligencePlatforms>;
}): ReactNode {
  return (
    <>
      {platforms.map((p) => (
        <div className="altplat-sec" key={p.id}>
          <div className="altplat-name">{platformName(p)}</div>
          {p.intelligence ? <PlatformIntelligence intelligence={p.intelligence} /> : null}
        </div>
      ))}
    </>
  );
}

const FORUM_SENTIMENT_COLORS: Record<string, string> = {
  positivo: 'var(--green-dark)',
  neutro: 'var(--text-3)',
  negativo: 'var(--red-dark)',
};

const TREND_ICON: Record<string, string> = { up: '▲', stable: '▬', down: '▼' };
const TREND_COLOR: Record<string, string> = {
  up: 'var(--red-dark)',
  stable: 'var(--text-3)',
  down: 'var(--green-dark)',
};

/** Il tipo lo dà il contratto: `trend` è facoltativo dal 23/10 (punto 19). */
type SentimentTheme = NonNullable<Level2['sentiment']>['positivi'][number];

function SentimentBody({ sentiment }: { sentiment: NonNullable<Level2['sentiment']> }): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <div className="sent-grid">
        <SentimentColumn items={sentiment.positivi} kind="pos" />
        <SentimentColumn items={sentiment.negativi} kind="neg" />
      </div>
      <div className="sent-note">{t('decision.sentimentNote')}</div>
      {/* v40: nota facoltativa in coda al blocco, HTML dal JSON come
          `analysis`. Nessun brand del dataset demo la popola. */}
      {sentiment.nota ? (
        <div style={{ marginTop: 18 }} dangerouslySetInnerHTML={{ __html: sentiment.nota }} />
      ) : null}
    </>
  );
}

/**
 * Colonna del sentiment. Le percentuali sono calcolate separatamente per
 * colonna e non sono confrontabili fra loro (spec §7.6, [CRITICA]): la nota
 * sotto la griglia lo dice esplicitamente e non va rimossa.
 */
function SentimentColumn({
  items,
  kind,
}: {
  items: SentimentTheme[];
  kind: 'pos' | 'neg';
}): ReactNode {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  /* `trend` è facoltativo dal 23/10 (punto 19): senza il dato il simbolo non
     si mostra. La colonna del simbolo resta però in piedi finché almeno un
     tema ce l'ha, perché `.sent-trend` ha larghezza fissa e togliere lo span
     solo a qualche riga spingerebbe le sue percentuali 20 px più a destra
     delle altre. Nessun tema con trend: via anche la colonna. */
  const colonnaTrend = items.some((item) => item.trend != null);

  return (
    <div className="sent-col">
      <div
        className="sent-head"
        style={{ color: kind === 'pos' ? 'var(--green-dark)' : 'var(--red-dark)' }}
      >
        {kind === 'pos' ? t('decision.whatWorks') : t('decision.whatDoesNot')}
      </div>
      {items.map((item) => (
        <div className="sent-row" key={item.tema}>
          <span className="sent-tema">{item.tema}</span>
          <span className="sent-pct">{`${item.pct}%`}</span>
          {colonnaTrend ? (
            <span
              className="sent-trend"
              style={{ color: TREND_COLOR[item.trend ?? ''] ?? 'var(--text-3)' }}
            >
              {TREND_ICON[item.trend ?? ''] ?? ''}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ForumBody({ forum }: { forum: NonNullable<Level2['forum']> }): ReactNode {
  return (
    <>
      <AcContent html={forum.content} blocks={forum.blocks} />
      {forum.temi && forum.temi.length > 0 ? (
        <div className="forum-temi">
          {forum.temi.map((tema) => (
            <div className="forum-tema" key={tema.tema}>
              <span
                className="forum-dot"
                style={{ background: FORUM_SENTIMENT_COLORS[tema.sentiment] }}
              />
              {tema.tema}
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * Card 08. Non ha più il ramo di ripiego: quando `fameRiskDetail` manca, la
 * card non si rende affatto (16/10, Level 2 punto 3), quindi qui il blocco
 * c'è per costruzione.
 */
function RiskDetailBody({ detail }: { detail: NonNullable<Level2['fameRiskDetail']> }): ReactNode {
  const { t } = useTranslation();

  return (
    <>
      <AcLabel>{t('decision.damagePerPhase')}</AcLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {detail.phases.map((phase) => (
          <div
            key={phase.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 14px',
              background: 'var(--cream)',
              borderRadius: 6,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--navy)',
                minWidth: 180,
              }}
            >
              {phase.name}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--text-3)',
                minWidth: 80,
              }}
            >
              {phase.period}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--ink)',
                marginLeft: 'auto',
              }}
            >
              {formatEuro(phase.min)} – {formatEuro(phase.max)}
            </span>
          </div>
        ))}
      </div>

      {detail.notaCPA ? (
        <>
          <AcLabel>{t('decision.notaCPA')}</AcLabel>
          <p>{detail.notaCPA}</p>
        </>
      ) : null}
      {detail.comparatori ? (
        <>
          <AcLabel>{t('decision.comparators')}</AcLabel>
          <p>{detail.comparatori}</p>
        </>
      ) : null}
      {detail.rischioGruppo && detail.rischioGruppo.length > 0 ? (
        <>
          <AcLabel>{t('decision.groupRisk')}</AcLabel>
          <AcList items={detail.rischioGruppo} />
        </>
      ) : null}
    </>
  );
}

function ActionPlanBody({ plan }: { plan: NonNullable<Level2['actionPlan']> }): ReactNode {
  return (
    <>
      {plan.areas.map((area) => (
        <div key={area.name}>
          <AcLabel>{area.name}</AcLabel>
          <AcList items={area.actions} />
        </div>
      ))}
    </>
  );
}

function RoadmapBody({ roadmap }: { roadmap: NonNullable<Level2['roadmap']> }): ReactNode {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {roadmap.phases.map((phase, i) => (
        <div
          key={phase.name}
          style={{
            padding: '14px 18px',
            background: 'var(--cream)',
            borderRadius: 8,
            borderLeft: '3px solid var(--orange)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                color: 'var(--orange)',
                fontWeight: 700,
              }}
            >
              {t('decision.phase')} {i + 1}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--navy)',
              }}
            >
              {phase.name}
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--text-3)',
                marginLeft: 'auto',
              }}
            >
              {phase.period}
            </span>
          </div>
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              lineHeight: 1.55,
              color: 'var(--text-2)',
              margin: 0,
            }}
          >
            {phase.desc}
          </p>
        </div>
      ))}
    </div>
  );
}
